import React from "react";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Icon rendered inside the field on the left. */
  icon?: React.ReactNode;
  /** Element rendered inside the field on the right (e.g. a show-password toggle). */
  trailing?: React.ReactNode;
  invalid?: boolean;
}

const fieldBase =
  "w-full rounded-lg bg-gray-900/60 border text-gray-100 placeholder-gray-500 " +
  "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/60 " +
  "focus:border-blue-500 disabled:opacity-60";

/**
 * Text input primitive. Replaces the repeated
 * `bg-gray-700 border-gray-600 focus:ring-blue-500 …` field markup.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ icon, trailing, invalid, className = "", ...rest }, ref) => {
    const border = invalid ? "border-red-500/70" : "border-gray-700";
    if (icon || trailing) {
      return (
        <div className="relative">
          {icon && (
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              {icon}
            </span>
          )}
          <input
            ref={ref}
            className={`${fieldBase} ${border} ${icon ? "pl-10" : "pl-3"} ${
              trailing ? "pr-10" : "pr-3"
            } py-2.5 ${className}`}
            {...rest}
          />
          {trailing && (
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
              {trailing}
            </span>
          )}
        </div>
      );
    }
    return (
      <input
        ref={ref}
        className={`${fieldBase} ${border} px-3 py-2.5 ${className}`}
        {...rest}
      />
    );
  }
);

Input.displayName = "Input";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

/** Multiline sibling of Input — same field treatment. */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ invalid, className = "", rows = 4, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={`${fieldBase} ${
        invalid ? "border-red-500/70" : "border-gray-700"
      } px-3 py-2.5 ${className}`}
      {...rest}
    />
  )
);

Textarea.displayName = "Textarea";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

/** Native select styled to match Input. */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ invalid, className = "", children, ...rest }, ref) => (
    <select
      ref={ref}
      className={`${fieldBase} ${
        invalid ? "border-red-500/70" : "border-gray-700"
      } px-3 py-2.5 ${className}`}
      {...rest}
    >
      {children}
    </select>
  )
);

Select.displayName = "Select";

export interface FieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** Label + control + hint/error wrapper for forms. */
export const Field: React.FC<FieldProps> = ({
  label,
  htmlFor,
  hint,
  error,
  className = "",
  children,
}) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && (
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-gray-300"
      >
        {label}
      </label>
    )}
    {children}
    {error ? (
      <p className="text-xs text-red-400">{error}</p>
    ) : (
      hint && <p className="text-xs text-gray-500">{hint}</p>
    )}
  </div>
);

export default Input;
